import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Button, Alert, Chip, IconButton, TextField, InputAdornment } from '@mui/material';
import { ArrowBack, PictureAsPdf, Search, Clear } from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; // CORRECT IMPORT
import { fmt, sym } from '../utils/currency';
import ConfirmDialog from './ConfirmDialog';

const GLEntries = () => {
  const { accountNo } = useParams();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [accountName, setAccountName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [confirmRev, setConfirmRev] = useState(null); // entry pending reversal
  const [reversing, setReversing] = useState(false);
  // Filter + sort. Default: chronological (date asc) so the running Balance reads
  // correctly. Sorting by another column keeps each entry's own posted balance.
  const [search, setSearch]   = useState('');
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  const [sortBy, setSortBy]   = useState('date');
  const [sortDir, setSortDir] = useState('asc');

  const fetchData = useCallback(async () => {
    try {
      const [glRes, accRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/finance/gl-entries/${accountNo}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/finance/gl-accounts`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      setEntries(glRes.data);
      const acc = accRes.data.find(a => a.accountNo === accountNo);
      setAccountName(acc ? acc.accountName : accountNo);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load entries');
    }
  }, [accountNo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reverse the invoice/receipt behind a GL entry — confirmed via dialog to avoid
  // accidental reversal. Same endpoint as the Finance Dashboard.
  const doReverse = async () => {
    if (!confirmRev) return;
    setReversing(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/finance/reverse/${confirmRev.docType}/${confirmRev.docId}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setMessage(`${confirmRev.docType === 'invoice' ? 'Invoice' : 'Receipt'} reversed successfully`);
      setConfirmRev(null);
      fetchData();
    } catch (err) {
      setMessage(`Error reversing: ${err.response?.data?.error || err.message}`);
      setConfirmRev(null);
    } finally {
      setReversing(false);
    }
  };

  // Current account balance = last chronological entry, regardless of any filter.
  const finalBalance = entries.length > 0 ? entries[entries.length - 1].balance : 0;

  const handleSort = (field) => {
    if (sortBy !== field) { setSortBy(field); setSortDir('asc'); }
    else setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
  };

  // The rows actually shown: filtered by search (doc no / description) + date
  // range, then sorted by the active column.
  const visibleEntries = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromT = from ? new Date(from).getTime() : null;
    const toT   = to   ? new Date(to + 'T23:59:59').getTime() : null;
    const rows = entries.filter(e => {
      if (q) {
        // Search across every visible field: doc no, description, the amounts
        // (both plain "1500.00" and grouped "1,500.00"), and the date — so any
        // given transaction can be found by whatever the user remembers.
        const hay = [
          e.documentNo, e.description,
          e.debit || '', e.credit || '', e.balance,
          fmt(e.debit || 0), fmt(e.credit || 0), fmt(e.balance || 0),
          new Date(e.date).toLocaleDateString(),
        ].map(v => String(v ?? '').toLowerCase()).join('|');
        if (!hay.includes(q)) return false;
      }
      const t = new Date(e.date).getTime();
      if (fromT !== null && t < fromT) return false;
      if (toT   !== null && t > toT)   return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (e) => {
      if (sortBy === 'date')    return new Date(e.date).getTime();
      if (sortBy === 'debit')   return e.debit || 0;
      if (sortBy === 'credit')  return e.credit || 0;
      if (sortBy === 'balance') return e.balance || 0;
      return String(e[sortBy] ?? '').toLowerCase(); // documentNo, description
    };
    return [...rows].sort((a, b) => {
      const x = val(a), y = val(b);
      if (typeof x === 'number') return (x - y) * dir;
      return x.localeCompare(y, undefined, { numeric: true }) * dir;
    });
  }, [entries, search, from, to, sortBy, sortDir]);

  // Sortable header cell.
  const head = (field, label, align) => (
    <TableCell align={align} sortDirection={sortBy === field ? sortDir : false}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}>
        <strong>{label}</strong>
      </TableSortLabel>
    </TableCell>
  );

  // PDF EXPORT FUNCTION
  const exportPDF = () => {
    const doc = new jsPDF();
    const title = `GL Entries - ${accountNo} - ${accountName}`;
    const balanceText = `Balance: ${sym()} ${fmt(finalBalance)}`;

    doc.setFontSize(16);
    doc.text(title, 14, 22);
    doc.setFontSize(12);
    doc.text(balanceText, 14, 30);

    const tableData = visibleEntries.map(e => [
      new Date(e.date).toLocaleDateString(),
      e.documentNo || '',
      e.description || '',
      e.debit > 0 ? e.debit.toFixed(2) : '',
      e.credit > 0 ? e.credit.toFixed(2) : '',
      e.balance.toFixed(2)
    ]);

    // Use autoTable as function
    autoTable(doc, {
      head: [['Date', 'Document No', 'Description', 'Debit', 'Credit', 'Balance']],
      body: tableData,
      startY: 40,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 30 },
        2: { cellWidth: 60 },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right', fontStyle: 'bold' }
      }
    });

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.text(`Page ${i} of ${pageCount}`, 180, 290);
    }

    doc.save(`GL_Entries_${accountNo}.pdf`);
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Button startIcon={<ArrowBack />} onClick={() => navigate('/gl-accounts')} sx={{ mr: 2 }}>
                Back to GL
              </Button>
              <Typography variant="h5" color="primary">
                General Ledger
              </Typography>
            </Box>

            <IconButton
              color="error"
              onClick={exportPDF}
              title="Export to PDF"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}
            >
              <PictureAsPdf />
            </IconButton>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              {accountNo} - {accountName}
            </Typography>
            <Chip
              label={`Balance: ${sym()} ${fmt(finalBalance)}`}
              color={finalBalance >= 0 ? 'success' : 'error'}
              variant="outlined"
              sx={{ fontWeight: 'bold', fontSize: '1rem' }}
            />
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {message && (
            <Alert severity={message.startsWith('Error') ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMessage('')}>
              {message}
            </Alert>
          )}

          {/* Filter bar */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            <TextField size="small" placeholder="Search doc no, description, amount or date…" value={search}
              onChange={e => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}><Clear fontSize="small" /></IconButton>
                  </InputAdornment>
                ),
              }} />
            <TextField size="small" type="date" label="From" value={from}
              onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="To" value={to}
              onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Typography variant="caption" color="text.secondary">
              {visibleEntries.length} of {entries.length}
            </Typography>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {head('date', 'Date')}
                {head('documentNo', 'Document No')}
                {head('description', 'Description')}
                {head('debit', 'Debit', 'right')}
                {head('credit', 'Credit', 'right')}
                {head('balance', 'Balance', 'right')}
                <TableCell><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleEntries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                    {entries.length === 0 ? 'No entries.' : 'No entries match your filters.'}
                  </TableCell>
                </TableRow>
              )}
              {visibleEntries.map((e, i) => (
                <TableRow key={e.entryNo || i} hover>
                  <TableCell>{new Date(e.date).toLocaleDateString()}</TableCell>
                  <TableCell>{e.documentNo || ''}</TableCell>
                  <TableCell>{e.description || ''}</TableCell>
                  <TableCell align="right">{e.debit > 0 ? e.debit.toFixed(2) : ''}</TableCell>
                  <TableCell align="right">{e.credit > 0 ? e.credit.toFixed(2) : ''}</TableCell>
                  <TableCell align="right">
                    {e.balance.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {e.docStatus === 'reversed' ? (
                      <Chip label="Reversed" size="small" />
                    ) : e.docType ? (
                      <Button
                        size="small"
                        color="secondary"
                        variant="outlined"
                        onClick={() => setConfirmRev({ docType: e.docType, docId: e.docId, documentNo: e.documentNo })}
                      >
                        Reverse
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!confirmRev}
        title="Reverse this entry?"
        message={confirmRev
          ? `This reverses ${confirmRev.documentNo} and posts balancing entries. This cannot be undone.`
          : ''}
        confirmLabel="Yes, reverse"
        cancelLabel="No"
        confirmColor="error"
        loading={reversing}
        onConfirm={doReverse}
        onCancel={() => setConfirmRev(null)}
      />
    </Box>
  );
};

export default GLEntries;